import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Technologies from "../components/Technologies";
import { educationTimeline, experienceTimeline } from "../assets/data";

const AnimatedNumbers = ({ value }) => {
  const ref = useRef(null);

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration: 2000 });
  const isINView = useInView(ref);

  useEffect(() => {
    if (isINView) {
      motionValue.set(value);
    }
  }, [isINView, value, motionValue]);

  useEffect(() => {
    springValue.onChange((latest) => {
      if (ref.current && latest.toFixed(0) <= value) {
        ref.current.textContent = `${latest.toFixed(0)}+`;
      }
    });
  }, [springValue, value]);

  return <span ref={ref}></span>;
};

const TimelineItem = ({ item, index, category }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      className={`timeline-item ${category}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="timeline-content">
        <div className="timeline-marker">
          <div className="timeline-dot"></div>
          <div className="timeline-line"></div>
        </div>

        <div className="timeline-details">
          <div className="timeline-header">
            <div className="timeline-header-top">
              <span className="timeline-date">{item.date}</span>
              <div className={`tag ${category}`}>{category}</div>
            </div>
            <h3>{item.cardTitle}</h3>
          </div>

          <motion.div
            className="timeline-body"
            initial={false}
            animate={{
              height: isExpanded ? "auto" : "0px",
              opacity: isExpanded ? 1 : 0,
              marginTop: isExpanded ? "0.75rem" : "0rem",
            }}
            transition={{ duration: 0.3 }}
          >
            <p>{item.cardDetailedText}</p>
          </motion.div>

          <div className="timeline-footer">
            <motion.div
              className="expand-indicator"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const SectionTabs = ({ activeTab, setActiveTab }) => {
  return (
    <div className="section-tabs">
      <button
        className={activeTab === "experience" ? "active" : ""}
        onClick={() => setActiveTab("experience")}
      >
        EXPERIENCE
      </button>
      <button
        className={activeTab === "education" ? "active" : ""}
        onClick={() => setActiveTab("education")}
      >
        EDUCATION
      </button>
    </div>
  );
};

const About = () => {
  const [activeTab, setActiveTab] = useState("experience");

  return (
    <>
      <StyledAbout>
        <motion.div
          className="page-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1>ABOUT ME</h1>
          <motion.div
            className="title-bar"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </motion.div>        <div className="section-bio">
          <div className="bio-content">
            <motion.div
              className="bio-heading"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2>DEVELOPER <span>&</span><br />DESIGNER</h2>
              <div className="bio-text">
                <p>
                  Hi, I'm <strong>Shoumik</strong>, a web developer and UI/UX
                  designer with a passion for creating beautiful, functional, and
                  user-centered digital experiences. With{" "}
                  {new Date().getFullYear() - 2021} years of experience in the
                  field. I am always looking for new and innovative ways to bring
                  my clients' visions to life.
                </p>
                <p>
                  Whether I'm working on a website, mobile app, or other digital
                  product, I bring my commitment to design excellence and
                  user-centered thinking to every project I work on.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="bio-mission"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="quote-container">
                <div className="quote-mark">"</div>
                <p className="quote-text">
                  I believe that design is about more than just making things look
                  pretty – it's about solving problems and creating intuitive,
                  enjoyable experiences for users that leave a lasting impression.
                </p>
                <div className="quote-line"></div>
                <p className="quote-author">My Design Philosophy</p>
              </div>
            </motion.div>
          </div>

          <motion.div
            className="stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedNumbers value={20} />
              </div>
              <div className="stat-label">CLIENTS</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedNumbers value={10} />
              </div>
              <div className="stat-label">PROJECTS</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">
                <AnimatedNumbers value={new Date().getFullYear() - 2021} />
              </div>
              <div className="stat-label">YEARS</div>
            </div>
          </motion.div>
        </div>

        {/* Timeline Section with data from data.js */}
        <motion.div
          className="section-timeline-combined"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <SectionTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          <motion.div
            className="section-bar"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          />

          <div className="timeline-container">
            <motion.div
              className="timeline-track"
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {activeTab === "experience" && experienceTimeline.length > 0
                ? experienceTimeline.map((item, index) => (
                  <TimelineItem
                    key={index}
                    item={item}
                    index={index}
                    category="experience"
                  />
                ))
                : activeTab === "education" && educationTimeline.length > 0
                ? educationTimeline.map((item, index) => (
                  <TimelineItem
                    key={index}
                    item={item}
                    index={index}
                    category="education"
                  />
                ))
                : (
                  <div className="timeline-empty">
                    <p>No {activeTab} data available.</p>
                  </div>
                )}
            </motion.div>
          </div>
        </motion.div>

        {/* Technologies Section */}
        <Technologies />
      </StyledAbout>
    </>
  );
};

const StyledAbout = styled.div`
  min-height: 100vh;
  padding: 4rem 0;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 5rem;

  .page-title {
    text-align: center;
    margin-bottom: 1rem;

    h1 {
      font-size: 2.5rem;
      font-weight: 800;
      letter-spacing: 0.15em;
      color: var(--heading-text);
      margin-bottom: 0.75rem;
    }

    .title-bar {
      height: 3px;
      width: 80px;
      margin: 0 auto;
      background: linear-gradient(
        90deg,
        var(--vibrant, #4a5bef) 0%,
        var(--accent-color, #61DAFB) 100%
      );
      border-radius: 2px;
      transform-origin: center;
    }
  }
  .section-bio {
    display: flex;
    flex-direction: column;
    gap: 3rem;

    .bio-content {
      display: flex;
      flex-wrap: wrap;
      gap: 3rem;
      align-items: center;

      .bio-heading {
        flex: 1;
        min-width: 300px;
        
        h2 {
          font-size: 3.2rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 2rem;
          color: var(--heading-text);
          letter-spacing: 0.05em;
          
          span {
            color: var(--accent-color, #61DAFB);
          }
        }
      }

      .bio-text {
        p {
          color: var(--secondary-text);
          font-size: 1rem;
          line-height: 1.8;
          margin-bottom: 1.5rem;
          letter-spacing: 0.05em;

          strong {
            color: var(--primary-text);
            font-weight: 600;
          }
        }
      }
      
      .bio-mission {
        flex: 1;
        min-width: 300px;
        
        .quote-container {
          position: relative;
          padding: 2rem;
          background: rgba(var(--border-color-rgb), 0.05);
          border-radius: 12px;
          border-left: 4px solid var(--accent-color, #61DAFB);
          
          .quote-mark {
            font-size: 5rem;
            line-height: 0;
            position: absolute;
            top: 1.5rem;
            left: 1rem;
            color: rgba(var(--accent-color-rgb, 97, 218, 251), 0.1);
            font-family: serif;
            font-weight: 700;
          }
          
          .quote-text {
            font-size: 1.1rem;
            line-height: 1.8;
            color: var(--primary-text);
            font-style: italic;
            margin-bottom: 1.5rem;
            letter-spacing: 0.05em;
          }
          
          .quote-line {
            height: 2px;
            width: 60px;
            background: var(--accent-color, #61DAFB);
            margin: 1rem 0;
          }
          
          .quote-author {
            font-size: 0.9rem;
            font-weight: 600;
            letter-spacing: 0.15em;
            color: var(--accent-color, #61DAFB);
            text-transform: uppercase;
          }
        }
      }
    }

    .stats {
      display: flex;
      justify-content: space-around;
      flex-wrap: wrap;
      gap: 1.5rem;
      margin-top: 1rem;

      .stat-item {
        text-align: center;
        padding: 1.5rem;
        min-width: 160px;

        .stat-number {
          font-size: 2.8rem;
          font-weight: 700;
          color: var(--primary-text);
          margin-bottom: 0.5rem;
          letter-spacing: 0.03em;
        }

        .stat-label {
          font-size: 0.85rem;
          letter-spacing: 0.15em;
          color: var(--secondary-text);
          font-weight: 500;
        }
      }
    }
  }

  /* Timeline Styles */
  .section-timeline-combined {
    width: 100%;
    margin-bottom: 2rem;

    .section-tabs {
      display: flex;
      justify-content: center;
      margin-bottom: 1.5rem;
      gap: 3rem;

      button {
        background: transparent;
        border: none;
        color: var(--secondary-text);
        font-size: 1.2rem;
        font-weight: 600;
        letter-spacing: 0.15em;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 0.5rem 0;
        position: relative;
        box-shadow: none;

        &::after {
          content: "";
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--accent-color, #61DAFB);
          transition: width 0.3s ease;
        }

        &:hover {
          color: var(--primary-text);

          &::after {
            width: 40%;
          }
        }

        &.active {
          color: var(--primary-text);

          &::after {
            width: 100%;
          }
        }
      }
    }

    .section-bar {
      height: 2px;
      width: 60px;
      margin: 0 auto 3rem;
      background: var(--accent-color, #61DAFB);
      border-radius: 1px;
      transform-origin: center;
    }
  }

  .timeline-container {
    width: 100%;
    padding: 1rem 0;
    position: relative;
  }

  .timeline-track {
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 700px;
    margin: 0 auto;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 12px;
      top: 0;
      height: 100%;
      width: 1px;
      background-color: rgba(var(--border-color-rgb), 0.2);
      z-index: 0;
    }
  }

  .timeline-item {
    position: relative;
    padding: 0.75rem 0;
    cursor: pointer;

    .timeline-content {
      display: flex;
      align-items: flex-start;
      gap: 2rem;
    }

    .timeline-marker {
      position: relative;
      padding-right: 1rem;

      .timeline-dot {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1px solid var(--accent-color, #61DAFB);
        background-color: rgba(var(--primary-background-rgb), 1);
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 2;

        &::before {
          content: "";
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: var(--accent-color, #61DAFB);
          transition: all 0.3s ease;
        }
      }
    }

    .timeline-details {
      flex: 1;
      border-left: none;
      padding: 0.25rem 0;
      transition: all 0.3s ease;

      .timeline-header {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;

        .timeline-header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.3rem;
          
          .tag {
            font-size: 0.7rem;
            font-weight: 500;
            padding: 3px 8px;
            border-radius: 12px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            
            &.education {
              background: rgba(97, 218, 251, 0.1);
              color: #61DAFB;
            }
            
            &.experience {
              background: rgba(130, 80, 223, 0.1);
              color: #8250DF;
            }
          }
        }

        h3 {
          font-size: 1.1rem;
          color: var(--primary-text);
          font-weight: 600;
          margin: 0;
          letter-spacing: 0.03em;
          line-height: 1.4;
        }
      }

      .timeline-body {
        overflow: hidden;

        p {
          margin: 0;
          color: var(--secondary-text);
          font-size: 0.9rem;
          line-height: 1.6;
          letter-spacing: 0.05em;
        }
      }

      .timeline-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-top: 0.75rem;

        .expand-indicator {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--secondary-text);
        }
      }
    }

    &:hover {
      .timeline-dot {
        transform: scale(1.1);

        &::before {
          transform: scale(1.2);
        }
      }
    }
  }

  .timeline-item.experience {
    .timeline-dot {
      border-color: #8250DF;

      &::before {
        background-color: #8250DF;
      }
    }
  }

  .timeline-item.education {
    .timeline-dot {
      border-color: #61DAFB;

      &::before {
        background-color: #61DAFB;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 3rem 1rem;
    gap: 3rem;

    .page-title h1 {
      font-size: 2rem;
    }

    .section-bio .stats .stat-item {
      padding: 1rem;
      min-width: 120px;

      .stat-number {
        font-size: 2rem;
      }
    }

    .section-timeline-combined {
      .section-tabs {
        gap: 2rem;

        button {
          font-size: 1rem;
        }
      }
    }

    .timeline-track {
      &::before {
        left: 10px;
      }
    }

    .timeline-item {
      .timeline-marker {
        .timeline-dot {
          width: 20px;
          height: 20px;

          &::before {
            width: 8px;
            height: 8px;
          }
        }
      }

      .timeline-details {
        .timeline-header {
          h3 {
            font-size: 1rem;
          }
        }
      }
    }

    .timeline-card {
      min-width: 260px;
      padding: 1.25rem;
    }
  }
`;

export default About;
